import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiCall } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { formatToLocalDateTime } from '../utils/date';

const TransactionsComponent = ({ userId, accessToken, onRefresh, showTitle = true }) => {
    const { refreshAccessToken } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const fetchTransactionsRef = useRef(null);

    const fetchTransactions = useCallback(async (token = accessToken) => {
        if (!userId || !token) {
            console.log('[TransactionsComponent] Missing userId or accessToken, skipping fetch');
            setError('Please sign in to view transactions.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        console.log('[TransactionsComponent] Fetching transactions for userId:', userId, 'with accessToken:', token);
        try {
            const data = await apiCall(
                '/wallet/transactions?product_id=2&skip=0&limit=100',
                'GET',
                null,
                true, // Use Aurum API
                null, // No x-user-id header
                token
            );
            console.log('[TransactionsComponent] API response:', data);
            const transactionData = Array.isArray(data.data || data) ? (data.data || data) : [];
            setTransactions(transactionData);
            setError('');
        } catch (error) {
            console.error('[TransactionsComponent] Error fetching transactions:', error);
            console.log('[TransactionsComponent] Error details:', {
                message: error.message,
                status: error.status,
                response: error.response,
            });
            if (error.message.includes('401')) {
                try {
                    console.log('[TransactionsComponent] Attempting token refresh');
                    const newToken = await refreshAccessToken();
                    await fetchTransactions(newToken); // Retry with new token
                } catch (refreshError) {
                    console.error('[TransactionsComponent] Token refresh failed:', refreshError);
                    setError('Authentication failed. Please sign in again.');
                    window.location.href = '/';
                }
            } else if (
                error.status === 404 ||
                error.message.toLowerCase().includes('not found') ||
                error.message.includes('API Error: 404')
            ) {
                console.log('[TransactionsComponent] Handling 404 as empty transactions');
                setTransactions([]);
                setError('');
            } else {
                const errorMessage = error.response?.detail || error.message || 'Failed to fetch transactions';
                setError(`Error fetching transactions: ${errorMessage}`);
            }
        } finally {
            setIsLoading(false);
        }
    }, [userId, accessToken, refreshAccessToken]);

    useEffect(() => {
        fetchTransactionsRef.current = fetchTransactions;
        if (onRefresh) {
            console.log('[TransactionsComponent] Setting onRefresh callback');
            onRefresh(fetchTransactionsRef);
        }
    }, [fetchTransactions, onRefresh]);

    useEffect(() => {
        console.log('[TransactionsComponent] Initial fetch on mount');
        fetchTransactions();
    }, [fetchTransactions]);

    if (!userId || !accessToken) {
        console.log('[TransactionsComponent] Render: No userId or accessToken, showing sign-in message');
        return <p>Please sign in to view transactions.</p>;
    }

    console.log('[TransactionsComponent] Rendering with transactions:', transactions, 'error:', error);
    return (
        <div className="transactions-container">
            {showTitle && <h2 className="transactions-container__title">Transaction History</h2>}
            {isLoading && <p>Loading transactions...</p>}
            {error && <p className="error-text">{error}</p>}
            {transactions.length ? (
                <ul>
                    {transactions.map((tx) => {
                        const formattedDate = formatToLocalDateTime(tx.created_at);
                        const numericPoints = Number(tx.points_changed);
                        const isNumericPoints = Number.isFinite(numericPoints);
                        const formattedPoints = isNumericPoints
                            ? Math.abs(numericPoints).toLocaleString()
                            : (tx.points_changed ?? 'Points unavailable');
                        const pointsSummary = isNumericPoints
                            ? `${formattedPoints} points`
                            : formattedPoints;

                        return (
                            <li key={tx.id}>
                                <div className="transaction-item">
                                    <div className="transaction-item__summary">
                                        <span className="transaction-item__type">{tx.type}</span>
                                        <span className="transaction-item__points">{pointsSummary}</span>
                                        {tx.offer_code && (
                                            <span className="transaction-item__offer">Offer: {tx.offer_code}</span>
                                        )}
                                    </div>
                                    <span className="transaction-item__meta">{formattedDate}</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p>{isLoading ? 'Loading...' : 'No transactions found.'}</p>
            )}
        </div>
    );
};

export default TransactionsComponent;